import { AppBar, createStyles, Dialog, Theme, withStyles, WithStyles } from "@material-ui/core"
import { AppBarProps } from "@material-ui/core/AppBar"
import React from "react"

import { compose } from "react-apollo"
import { isMobile } from "../../utils/style"
import withIsMobile from "../hocs/withIsMobile"

const styles = (theme: Theme) =>
    createStyles({
        dialog: {
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
        },
        dialogInner: {
            flex: 1,
            flexDirection: "column",
            display: "flex",
            maxHeight: "calc(100vh - 96px)",
            [isMobile(theme)]: {
                maxHeight: "100vh",
                height: "100%",
            },
        },
        header: {
            flexShrink: 0,
            padding: "1rem",
            borderRadius: "10px 10px 0px 0px",
            [isMobile(theme)]: {
                borderRadius: "0px",
            },
        },
        body: {
            flex: 1,
            flexDirection: "column",
            justifyContent: "center",
            overflowY: "auto",
            flexGrow: 1,
        },
        footer: {
            width: "100%",
            backgroundColor: "white",
            height: "72px",
            [isMobile(theme)]: {
                height: "64px",
            },
            display: "flex",
            bottom: 0,
            borderTop: "1px solid rgba(0, 0, 0, 0.12)",
            paddingTop: theme.spacing(1),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            flexShrink: 0,
        },
    })

interface ChildrenProp {
    dialogBodyRef: React.RefObject<HTMLDivElement> | null
}

interface Props extends WithStyles<typeof styles> {
    headerCssPosition?: AppBarProps["position"]
    headerHasShadow?: boolean
    disableBackdropClick?: boolean
    disableEscapeKeyDown?: boolean
    header?: React.ReactNode
    zIndex?: number
    children: (props: ChildrenProp) => React.ReactNode | React.ReactNode
    footer?: (dialogBodyRef: React.RefObject<HTMLDivElement>) => React.ReactNode | React.ReactNode
    isMobile: boolean
    handleClose?: () => void
    open: boolean
    onBodyScrollCallback?: (e: React.UIEvent) => void
}

class AppDialog extends React.PureComponent<Props> {
    dialogBodyRef: React.RefObject<HTMLDivElement> = React.createRef()

    static defaultProps = {
        disableBackdropClick: false,
        disableEscapeKeyDown: false,
    }

    render() {
        const { classes } = this.props

        if (!this.props.open) {
            return false
        }

        return (
            <Dialog
                disablePortal={true}
                fullScreen={this.props.isMobile}
                onClose={this.props.handleClose}
                fullWidth={true}
                open={this.props.open}
                transitionDuration={0}
                disableBackdropClick={this.props.disableBackdropClick}
                disableEscapeKeyDown={this.props.disableEscapeKeyDown}
                style={{ zIndex: this.props.zIndex || 1200 }}>
                <div className={classes.dialog}>
                    <div className={classes.dialogInner}>
                        {this.renderHeader()}
                        {this.renderBody()}
                        {this.renderFooter()}
                    </div>
                </div>
            </Dialog>
        )
    }

    renderHeader = () =>
        this.props.header && (
            <AppBar
                position={this.props.headerCssPosition || "static"}
                color="default"
                className={this.props.classes.header}
                style={{
                    ...(this.props.headerHasShadow ? { boxShadow: "rgba(0, 0, 0, 0.14) 0px 2px 10px" } : {}),
                }}>
                {this.props.header}
            </AppBar>
        )

    onBodyScroll = (e: React.UIEvent) => {
        if (this.props.onBodyScrollCallback) {
            this.props.onBodyScrollCallback(e)
        }
    }

    renderBody = () => (
        <div
            onScroll={this.onBodyScroll}
            ref={this.dialogBodyRef}
            className={this.props.classes.body}
            style={{ scrollBehavior: "smooth" }}>
            {this.props.children instanceof Function
                ? this.props.children({
                      dialogBodyRef: this.dialogBodyRef,
                  })
                : this.props.children}
        </div>
    )

    renderFooter = () => {
        if (this.props.footer instanceof Function) {
            return (
                this.props.footer && (
                    <div className={this.props.classes.footer}>{this.props.footer(this.dialogBodyRef)}</div>
                )
            )
        } else {
            return this.props.footer && <div className={this.props.classes.footer}>{this.props.footer}</div>
        }
    }
}

const composed = compose(withIsMobile, withStyles(styles, { withTheme: true }))

export default composed(AppDialog)
